import React, { useState } from "react";
import { MessageCircle, Mail, Instagram, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BRAND } from "../../data/content";
import { ROOM_INTEREST_OPTIONS, BUDGET_OPTIONS, PEOPLE_OPTIONS } from "../../data/leadOptions";
import { Reveal, Overline } from "../shared/Primitives";
import { Sprig, DotGrid } from "../shared/Decor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    nombre: "",
    correo: "",
    telefono: "",
    habitacion_interes: "",
    fecha_entrada: "",
    presupuesto: "",
    num_personas: "",
    mensaje: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.correo) {
      toast.error("Por favor completa tu nombre y correo.");
      return;
    }
    if (!form.habitacion_interes || !form.fecha_entrada || !form.presupuesto || !form.num_personas) {
      toast.error("Completa habitación, fecha, presupuesto y número de personas.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await axios.post(`${API}/contact`, { ...form, source: "Formulario de contacto" });
      toast.success(data?.message || "Gracias por contactarnos. Nuestro equipo se comunicará contigo a la brevedad.");
      setForm({ nombre: "", correo: "", telefono: "", habitacion_interes: "", fecha_entrada: "", presupuesto: "", num_personas: "", mensaje: "" });
    } catch (err) {
      toast.error("Ocurrió un error al enviar. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full border-b border-khaki/70 py-3 bg-transparent text-espresso placeholder:text-camel focus:outline-none focus:border-cocoa transition-colors font-light";

  return (
    <section id="contacto" data-testid="contact-section" className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-28">
      <Sprig className="absolute top-10 left-2 w-20 text-camel/20 -rotate-12 hidden lg:block" />
      <DotGrid className="absolute bottom-12 right-10 w-24 text-camel/25 hidden sm:block" />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <Reveal><Overline>Estamos para ayudarte</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-espresso leading-tight">
                Hablemos de tu nuevo hogar
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-md text-lg font-light text-espresso/60 leading-relaxed">
                Contáctanos por el medio que prefieras. Con gusto te damos información sobre disponibilidad y el proceso de renta.
              </p>
            </Reveal>

            <div className="mt-12 space-y-6">
              <Reveal delay={0.15}>
                <a
                  data-testid="contact-whatsapp"
                  href={`https://wa.me/${BRAND.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-5 border-b border-khaki/40 pb-6"
                >
                  <MessageCircle size={26} strokeWidth={1.5} className="text-cocoa" />
                  <div>
                    <span className="block text-xs font-light text-camel uppercase tracking-wide">WhatsApp</span>
                    <span className="text-lg font-bold text-espresso group-hover:text-cocoa transition-colors">{BRAND.whatsappDisplay}</span>
                  </div>
                  <ArrowRight size={18} className="ml-auto text-camel group-hover:text-cocoa group-hover:translate-x-1 transition-all" />
                </a>
              </Reveal>

              <Reveal delay={0.2}>
                <a
                  data-testid="contact-email"
                  href={`mailto:${BRAND.email}`}
                  className="group flex items-center gap-5 border-b border-khaki/40 pb-6"
                >
                  <Mail size={26} strokeWidth={1.5} className="text-cocoa" />
                  <div>
                    <span className="block text-xs font-light text-camel uppercase tracking-wide">Correo</span>
                    <span className="text-lg font-bold text-espresso group-hover:text-cocoa transition-colors">{BRAND.email}</span>
                  </div>
                  <ArrowRight size={18} className="ml-auto text-camel group-hover:text-cocoa group-hover:translate-x-1 transition-all" />
                </a>
              </Reveal>

              {BRAND.showInstagram && (
                <Reveal delay={0.25}>
                  <a
                    data-testid="contact-instagram"
                    href={BRAND.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-5 border-b border-khaki/40 pb-6"
                  >
                    <Instagram size={26} strokeWidth={1.5} className="text-cocoa" />
                    <div>
                      <span className="block text-xs font-light text-camel uppercase tracking-wide">Instagram</span>
                      <span className="text-lg font-bold text-espresso group-hover:text-cocoa transition-colors">{BRAND.instagram}</span>
                    </div>
                    <ArrowRight size={18} className="ml-auto text-camel group-hover:text-cocoa group-hover:translate-x-1 transition-all" />
                  </a>
                </Reveal>
              )}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="bg-linen border border-khaki/50 rounded-2xl p-8 sm:p-12">
              <h3 className="text-xl font-bold text-espresso">Envíanos un mensaje</h3>
              <form onSubmit={handleSubmit} className="mt-8 space-y-7" data-testid="contact-form">
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Nombre</label>
                  <input data-testid="form-nombre" name="nombre" value={form.nombre} onChange={handleChange} placeholder="Tu nombre completo" className={inputClass} />
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Correo</label>
                  <input data-testid="form-correo" type="email" name="correo" value={form.correo} onChange={handleChange} placeholder="tu@correo.com" className={inputClass} />
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Teléfono</label>
                  <input data-testid="form-telefono" name="telefono" value={form.telefono} onChange={handleChange} placeholder="55 0000 0000" className={inputClass} />
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Habitación de interés *</label>
                  <select data-testid="form-habitacion" name="habitacion_interes" value={form.habitacion_interes} onChange={handleChange} className={`${inputClass} ${!form.habitacion_interes ? "text-camel" : ""}`}>
                    <option value="" disabled>Selecciona una opción</option>
                    {ROOM_INTEREST_OPTIONS.map((o) => (<option key={o} value={o} className="text-espresso">{o}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Número de personas *</label>
                  <select data-testid="form-personas" name="num_personas" value={form.num_personas} onChange={handleChange} className={`${inputClass} ${!form.num_personas ? "text-camel" : ""}`}>
                    <option value="" disabled>Selecciona una opción</option>
                    {PEOPLE_OPTIONS.map((o) => (<option key={o} value={o} className="text-espresso">{o}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Fecha deseada de entrada *</label>
                  <input data-testid="form-fecha" type="date" name="fecha_entrada" value={form.fecha_entrada} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Presupuesto mensual *</label>
                  <select data-testid="form-presupuesto" name="presupuesto" value={form.presupuesto} onChange={handleChange} className={`${inputClass} ${!form.presupuesto ? "text-camel" : ""}`}>
                    <option value="" disabled>Selecciona un rango</option>
                    {BUDGET_OPTIONS.map((o) => (<option key={o} value={o} className="text-espresso">{o}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-bold tracking-wide uppercase text-camel">Mensaje</label>
                  <textarea data-testid="form-mensaje" name="mensaje" value={form.mensaje} onChange={handleChange} rows={3} placeholder="Cuéntanos qué necesitas" className={`${inputClass} resize-none`} />
                </div>
                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-cocoa text-linen px-8 py-4 text-sm font-bold tracking-wide uppercase hover:bg-espresso transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Enviando..." : "Enviar solicitud"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
